import { forwardRef } from 'react';
import { twMerge } from 'tailwind-merge';

import { Colors, Sizes, Variants } from '@/components/ui/types';

import { BadgeProps } from './types';

const sizeClasses: Record<Sizes, string> = {
  xs: 'badge-xs',
  sm: 'badge-sm',
  md: 'badge-md',
  lg: 'badge-lg',
  xl: 'badge-xl',
};

const colorClasses: Record<Colors, string> = {
  default: '',
  neutral: 'badge-neutral',
  primary: 'badge-primary',
  secondary: 'badge-secondary',
  accent: 'badge-accent',
  info: 'badge-info',
  success: 'badge-success',
  warning: 'badge-warning',
  error: 'badge-error',
};

const variantClasses: Record<Variants, string> = {
  default: '',
  outline: 'badge-outline',
  dash: 'badge-dash',
  soft: 'badge-soft',
  ghost: 'badge-ghost',
};

export const Badge = forwardRef<HTMLSpanElement, BadgeProps>(
  (
    {
      className,
      size = 'md',
      color = 'default',
      variant = 'default',
      children,
      ...props
    },
    ref,
  ) => {
    const isDefault = color === 'default' && variant === 'default';
    const isSoftWithDefaultColor = color === 'default' && variant === 'soft';
    const classes = twMerge(
      'badge',
      sizeClasses[size],
      colorClasses[color],
      variantClasses[variant],
      (isDefault || variant === 'soft') && 'badge-noise',
      isSoftWithDefaultColor && 'bg-base-200 border-base-content/5',
      isDefault && 'border-base-300',
      className,
    );

    return (
      <span ref={ref} className={classes} {...props}>
        {children}
      </span>
    );
  },
);

Badge.displayName = 'Badge';
